import { ElementType, FC, Fragment, ReactNode } from 'react';
import {
    Box,
    Divider,
    List,
    ListProps,
    ListItem as BaseListItem,
    ListItemAvatar,
    ListItemText,
    SvgIconProps,
    styled,
    ListItemProps,
    ListItemAvatarProps,
    ListItemTextProps,
    Tooltip,
} from '@mui/material';
import { AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon } from '@mui/icons-material';

import { useUserContext } from '../../hooks/Contexts/UserContext';
import IconContainer from '../Icon/IconContainer';

const ListItem = styled(BaseListItem)(({ theme }) => ({
    '&.MuiListItem-gutters': {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
    },
    '&.MuiListItem-padding': {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    '&.MuiListItemSecondaryAction-root': {
        right: theme.spacing(4),
    },
}));

interface OverviewListItem {
    icon: ElementType<SvgIconProps>;
    primary: ReactNode;
    secondary?: ReactNode;
    tertiary?: ReactNode;
    adminOnly?: boolean;
}

interface OverviewListProps extends ListProps {
    items: OverviewListItem[];
    slotProps?: {
        listItem?: ListItemProps;
        listItemAvatar?: ListItemAvatarProps;
        listItemText?: ListItemTextProps;
        tertiaryListItem?: ListItemProps;
    };
}

const OverviewList: FC<OverviewListProps> = ({ items, slotProps = {}, ...props }) => {
    const { userContext } = useUserContext();

    return (
        <List {...props}>
            {items
                .filter((item: OverviewListItem) => {
                    if (item.adminOnly) {
                        return userContext.isAdmin() || userContext.isOpsAdmin();
                    }
                    return true;
                })
                .map((item: OverviewListItem, index: number) => {
                    return (
                        <Fragment key={index}>
                            <ListItem alignItems="center" secondaryAction={item?.secondary} {...slotProps?.listItem}>
                                <ListItemAvatar sx={{ m: 0 }} {...slotProps?.listItemAvatar}>
                                    <IconContainer icon={item.icon} />
                                </ListItemAvatar>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ListItemText
                                        primary={item.primary}
                                        slotProps={{
                                            primary: { variant: 'body2' },
                                        }}
                                        {...slotProps?.listItemText}
                                    />

                                    {item.adminOnly && (
                                        <Tooltip title="Visible to Admins Only">
                                            <AdminPanelSettingsOutlinedIcon fontSize="small" color="primary" />
                                        </Tooltip>
                                    )}
                                </Box>
                            </ListItem>

                            {item?.tertiary && <ListItem {...slotProps?.tertiaryListItem}>{item?.tertiary}</ListItem>}
                            {index + 1 < items.length && <Divider component="li" />}
                        </Fragment>
                    );
                })}
        </List>
    );
};

export type { OverviewListItem };
export type { OverviewListProps };
export default OverviewList;
